import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import { Config } from '../../config';
import { useNotifications } from '../../contexts/NotificationContext';

import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import LoadingSpinner from '../../components/LoadingSpinner';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

import Card from '../../components/dashboard/Card';
import ChartCard from '../../components/dashboard/ChartCard';
import KpiCard from '../../components/dashboard/KpiCard';
import {
  lineChartOptions,
  lineDataset,
  horizontalBarChartOptions,
  severityBarDataset,
} from '../../utils/chartTheme';

import RegulationsCard from '../../partials/dashboard/RegulationsCard';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildTrendData(history) {
  if (!history || !Array.isArray(history) || history.length === 0) {
    return { labels: [], values: [] };
  }
  const sorted = [...history].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );
  return {
    labels: sorted.map((item) => format(parseISO(item.timestamp), 'dd MMM')),
    values: sorted.map((item) => Number(item.avg_compliance) || 0),
  };
}

function calcDelta(history) {
  if (!history || history.length < 2) return null;
  const sorted = [...history].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );
  const last = Number(sorted[sorted.length - 1].avg_compliance);
  const prev = Number(sorted[sorted.length - 2].avg_compliance);
  if (Number.isNaN(last) || Number.isNaN(prev)) return null;
  return last - prev;
}

function complianceAccent(value) {
  if (value >= 80) return 'success';
  if (value >= 60) return 'brand';
  if (value >= 40) return 'warning';
  return 'danger';
}

function timeAgo(timestamp) {
  const now = new Date();
  const ts = new Date(timestamp);
  const diff = Math.floor((now - ts) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `${m}m ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `${h}h ago`;
  }
  const d = Math.floor(diff / 86400);
  return `${d}d ago`;
}

// ---------------------------------------------------------------------------
// Sub-components (kept inline since they're Dashboard-specific)
// ---------------------------------------------------------------------------

function EmptyState({ message = 'No data available' }) {
  return (
    <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-slate-500 dark:text-slate-500">
      <svg className="w-10 h-10 mb-2 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      <p className="text-xs">{message}</p>
    </div>
  );
}

function NotificationsList({ notifications }) {
  const unread = notifications.filter((n) => !n.read);
  if (unread.length === 0) {
    return <EmptyState message="No pending notifications" />;
  }
  return (
    <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1 scrollbar-thin">
      {unread.map((n) => (
        <div
          key={n.id}
          className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-transparent hover:border-brand-500/30 transition-colors"
        >
          <div className="w-2 h-2 rounded-full bg-brand-400 mt-1.5 flex-shrink-0" />
          <div className="flex-grow min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                {n.title}
              </div>
              <div className="text-[10px] text-slate-400 dark:text-slate-500 flex-shrink-0">
                {timeAgo(n.timestamp)}
              </div>
            </div>
            {n.subtitle && (
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                {n.subtitle}
              </div>
            )}
            {n.message && (
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                {n.message}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ControlRow({ label, code, description, value, positive }) {
  const tone = positive
    ? 'text-emerald-400 bg-emerald-500/10'
    : 'text-rose-400 bg-rose-500/10';
  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500">
          {label}
        </div>
        <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
          {code}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {description?.split('\n')[0]}
        </div>
      </div>
      <div className={`shrink-0 px-3 py-1.5 rounded-md text-sm font-bold ${tone}`}>
        {value.toFixed(1)}%
      </div>
    </div>
  );
}

function ControlPerformance({ parentControls }) {
  if (!parentControls || Object.keys(parentControls).length === 0) {
    return <EmptyState />;
  }
  return (
    <div className="space-y-5 max-h-[320px] overflow-y-auto pr-1 scrollbar-thin">
      {Object.entries(parentControls).map(([framework, controls]) => {
        const entries = Object.entries(controls);
        if (entries.length === 0) return null;
        const sorted = [...entries].sort(
          (a, b) => b[1].compliance - a[1].compliance
        );
        const best = sorted[0];
        const worst = sorted[sorted.length - 1];
        const delta = best[1].compliance - worst[1].compliance;
        return (
          <div key={framework}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-brand-400">
                {framework}
              </span>
              <span className="text-[10px] text-slate-500 dark:text-slate-500">
                Δ {delta.toFixed(1)}%
              </span>
            </div>
            <div className="space-y-2">
              <ControlRow
                label="Highest compliance"
                code={best[0]}
                description={best[1].description}
                value={best[1].compliance}
                positive
              />
              <ControlRow
                label="Lowest compliance"
                code={worst[0]}
                description={worst[1].description}
                value={worst[1].compliance}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Dashboard
// ---------------------------------------------------------------------------

function GrcDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dashdata, setDashData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { updateTotalUnreadCount, notifications } = useNotifications();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { access_token: `Bearer ${token}` };

    setIsLoading(true);
    Promise.all([
      axios.get(`${Config.APIBaseURL}/dashdata/`, { headers, withCredentials: true }),
      Promise.resolve(null), // Messaging disabled
    ])
      .then(([dashRes, unreadRes]) => {
        setDashData(dashRes.data);
        if (unreadRes?.data?.count != null) {
          setUnreadCount(unreadRes.data.count);
          if (typeof updateTotalUnreadCount === 'function') {
            updateTotalUnreadCount(unreadRes.data.count);
          }
        }
      })
      .catch((err) => {
        console.error('Error fetching dashboard data:', err);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trend = useMemo(() => buildTrendData(dashdata?.history), [dashdata]);
  const delta = useMemo(() => calcDelta(dashdata?.history), [dashdata]);
  const compliance = Number(dashdata?.compliance) || 0;
  const frameworks = dashdata?.frameworks || {};
  const frameworkNames = Object.keys(frameworks);
  const historyCount = dashdata?.history?.length || 0;
  const unreadNotifications = notifications.filter((n) => !n.read).length;

  const trendChartData = useMemo(
    () => ({
      labels: trend.labels,
      datasets: [lineDataset('Compliance', trend.values)],
    }),
    [trend]
  );

  const trendOptions = useMemo(
    () =>
      lineChartOptions({
        tooltipLabel: (ctx) => `Compliance: ${ctx.parsed.y.toFixed(1)}%`,
      }),
    []
  );

  const frameworksChartData = useMemo(() => {
    const labels = frameworkNames;
    const values = labels.map((k) => Number(frameworks[k]) || 0);
    return {
      labels,
      datasets: [severityBarDataset('Compliance', values)],
    };
  }, [frameworkNames, frameworks]);

  const frameworksOptions = useMemo(
    () =>
      horizontalBarChartOptions({
        tooltipLabel: (ctx) => `${ctx.label}: ${ctx.parsed.x.toFixed(1)}%`,
      }),
    []
  );

  const handleNewAnalysis = () => navigate('/grc/gap-analysis?openModal=true');
  const handleViewReports = () => navigate('/reports');

  const trendDirection =
    delta == null ? 'flat' : delta > 0.1 ? 'up' : delta < -0.1 ? 'down' : 'flat';

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      {isLoading && <LoadingSpinner />}

      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            {/* Page title */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">GRC Dashboard</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Compliance across frameworks, and how it has moved.
              </p>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KpiCard
                label="Average Compliance"
                value={compliance}
                decimals={1}
                suffix="%"
                accent={complianceAccent(compliance)}
                trend={delta != null ? { value: delta, direction: trendDirection } : null}
                sub={delta != null ? 'vs previous snapshot' : 'no history yet'}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                }
              />
              <KpiCard
                label="Active Frameworks"
                value={frameworkNames.length}
                accent="brand"
                sub="Frameworks assessed"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                }
              />
              <KpiCard
                label="Snapshots"
                value={historyCount}
                accent="info"
                sub="Measurements recorded"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
              <KpiCard
                label="Notifications"
                value={unreadNotifications}
                accent={unreadNotifications > 0 ? 'warning' : 'info'}
                sub="Unread"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                }
              />
            </div>

            {/* Trend + Notifications */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <ChartCard
                title="Compliance Trend"
                subtitle="Historical evolution of the average"
                className="lg:col-span-2"
              >
                <div className="h-64">
                  {trend.values.length > 0 ? (
                    <Line data={trendChartData} options={trendOptions} />
                  ) : (
                    <EmptyState />
                  )}
                </div>
              </ChartCard>

              <ChartCard
                title="Notifications"
                subtitle={`${unreadNotifications} unread`}
              >
                <NotificationsList notifications={notifications} />
              </ChartCard>
            </div>

            {/* Frameworks compliance + Control performance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <ChartCard
                title="Compliance by Framework"
                subtitle="Comparison across assessed frameworks"
              >
                <div className="h-64">
                  {frameworkNames.length > 0 ? (
                    <Bar data={frameworksChartData} options={frameworksOptions} />
                  ) : (
                    <EmptyState />
                  )}
                </div>
              </ChartCard>

              <ChartCard
                title="Control Performance"
                subtitle="Highest and lowest per framework"
              >
                <ControlPerformance parentControls={dashdata?.parent_controls} />
              </ChartCard>
            </div>

            {/* Quick actions */}
            <Card className="p-4 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    Quick Actions
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Start a new analysis or jump to available reports.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleNewAnalysis}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    New Analysis
                  </button>
                  <button
                    type="button"
                    onClick={handleViewReports}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-sm font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View Reports
                  </button>
                </div>
              </div>
            </Card>

            {/* The CVE stream moved to the landing page, where it sits beside
                the exploitation status it gives context to. */}
            <div className="grid grid-cols-12 gap-6">
              <RegulationsCard />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default GrcDashboard;
